import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, ArrowDown, ArrowUp, ExternalLink, Plus, Trash2 } from "lucide-react";
import { rhm, apiError } from "../api";
import { Badge, Btn, Card, Field, Input, PageHead, Select, Textarea } from "../ui";
import { useRhmAuth } from "../RhmAuthContext";

const TABS = [
    { key: "products", label: "PRODUCT SCIENCE" },
    { key: "ingredients", label: "INGREDIENT LIBRARY" },
    { key: "settings", label: "PAGE & SEO" },
];

const TEXT_FIELDS = [
    ["overview", "Product overview"], ["philosophy", "Formulation philosophy"],
    ["unani", "Unani perspective"], ["ayurveda", "Ayurvedic perspective"],
    ["nutrition", "Nutrition science"], ["why_selected", "Why these ingredients were selected"],
    ["synergy", "How the ingredients complement each other"], ["usage", "Recommended use"],
    ["cautions", "Safety / cautions"], ["disclaimer", "Product-specific disclaimer"],
];

const IngredientRows = ({ rows, combos, onChange, editable }) => {
    const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    const move = (i, d) => {
        const j = i + d;
        if (j < 0 || j >= rows.length) return;
        const copy = [...rows];
        [copy[i], copy[j]] = [copy[j], copy[i]];
        onChange(copy);
    };
    return (
        <div className="space-y-2" data-testid="science-ingredients">
            {rows.map((r, i) => (
                <div key={r.id || i} className="border border-white/10 p-3" data-testid={`sci-ing-${i}`}
                    draggable={editable}
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", String(i))}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        const from = Number(e.dataTransfer.getData("text/plain"));
                        if (Number.isNaN(from) || from === i) return;
                        const copy = [...rows];
                        copy.splice(i, 0, copy.splice(from, 1)[0]);
                        onChange(copy);
                    }}>
                    <div className="grid md:grid-cols-[1fr_90px_70px_auto_auto_auto] gap-2 items-end">
                        <Field label={i === 0 ? "Ingredient" : ""}>
                            <Input value={r.name || ""} disabled={!editable} data-testid={`sci-ing-name-${i}`}
                                onChange={(e) => set(i, { name: e.target.value })} />
                        </Field>
                        <Field label={i === 0 ? "Dose" : ""}>
                            <Input value={r.dose || ""} disabled={!editable} data-testid={`sci-ing-dose-${i}`}
                                onChange={(e) => set(i, { dose: e.target.value })} />
                        </Field>
                        <Field label={i === 0 ? "Unit" : ""}>
                            <Input value={r.unit || ""} disabled={!editable}
                                onChange={(e) => set(i, { unit: e.target.value })} />
                        </Field>
                        <button onClick={() => set(i, { featured: !r.featured })} disabled={!editable}
                            data-testid={`sci-ing-featured-${i}`}
                            className={`mono text-[9px] tracking-[0.14em] border px-2 py-2 ${
                                r.featured ? "border-gold text-gold" : "border-white/12 text-zinc-500"}`}>
                            FEATURED
                        </button>
                        <button onClick={() => set(i, { visible: r.visible === false })} disabled={!editable}
                            data-testid={`sci-ing-visible-${i}`}
                            className={`mono text-[9px] tracking-[0.14em] border px-2 py-2 ${
                                r.visible === false ? "border-white/12 text-zinc-600" : "border-lime/40 text-lime"}`}>
                            {r.visible === false ? "HIDDEN" : "SHOWN"}
                        </button>
                        <div className="flex gap-1 pb-1">
                            <button onClick={() => move(i, -1)} disabled={!editable || i === 0}
                                data-testid={`sci-ing-up-${i}`} className="text-zinc-500 hover:text-white disabled:opacity-30">
                                <ArrowUp className="w-4 h-4" />
                            </button>
                            <button onClick={() => move(i, 1)} disabled={!editable || i === rows.length - 1}
                                data-testid={`sci-ing-down-${i}`} className="text-zinc-500 hover:text-white disabled:opacity-30">
                                <ArrowDown className="w-4 h-4" />
                            </button>
                            <button onClick={() => onChange(rows.filter((_, j) => j !== i))} disabled={!editable}
                                data-testid={`sci-ing-remove-${i}`} className="text-zinc-500 hover:text-red-400">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    {!!combos.length && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {combos.map((c) => {
                                const on = (r.combos || []).includes(c.id);
                                return (
                                    <button key={c.id} disabled={!editable} data-testid={`sci-ing-combo-${i}-${c.id}`}
                                        onClick={() => set(i, {
                                            combos: on ? r.combos.filter((x) => x !== c.id) : [...(r.combos || []), c.id],
                                        })}
                                        className={`mono text-[9px] tracking-[0.14em] border px-2 py-1 ${
                                            on ? "border-lime text-lime" : "border-white/12 text-zinc-500"}`}>
                                        {c.name.replace(" Combo", "").toUpperCase()}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            ))}
            <Btn variant="ghost" disabled={!editable} data-testid="sci-ing-add"
                onClick={() => onChange([...rows, { name: "", dose: "", unit: "mg", combos: [], featured: false, visible: true }])}>
                <Plus className="w-3 h-3 mr-1 inline" /> INGREDIENT
            </Btn>
        </div>
    );
};

const ProductEditor = ({ slug, meta, editable, onClose }) => {
    const qc = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: ["science-record", slug],
        queryFn: async () => (await rhm.get(`/science/products/${slug}`)).data,
    });
    const [form, setForm] = useState(null);
    useEffect(() => { if (data) setForm(data); }, [data]);

    const save = useMutation({
        mutationFn: async (body) => (await rhm.patch(`/science/products/${slug}`, body)).data,
        onSuccess: () => {
            toast.success("Science content saved");
            qc.invalidateQueries({ queryKey: ["science-record", slug] });
            qc.invalidateQueries({ queryKey: ["science-records"] });
        },
        onError: (e) => toast.error(apiError(e)),
    });

    if (isLoading || !form) return <Card>Loading…</Card>;
    const set = (patch) => setForm({ ...form, ...patch });
    const combos = ((data.formulation || {}).combos || []).map((c) => ({ id: c.id, name: c.name }));
    const locked = !editable || form.use_product_formulation;

    return (
        <div className="space-y-4" data-testid={`science-editor-${slug}`}>
            <PageHead title={`Science · ${form.product?.name || slug}`}
                subtitle={form.use_product_formulation
                    ? "Formulation comes from Product CRM — edit ingredients there. Science text lives here."
                    : "Product name, image and status come from Product CRM. Everything else is editable here."}
                action={<div className="flex gap-2">
                    <a href={`/science/${slug}`} target="_blank" rel="noreferrer" data-testid="science-view-public"
                        className="mono text-[10px] tracking-[0.16em] text-gold inline-flex items-center gap-1.5">
                        VIEW <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <Btn variant="ghost" onClick={onClose} data-testid="science-editor-close">BACK</Btn>
                    <Btn disabled={!editable || save.isPending} data-testid="science-save"
                        onClick={() => save.mutate({
                            ...Object.fromEntries(TEXT_FIELDS.map(([k]) => [k, form[k] || ""])),
                            traditional_system: form.traditional_system, category: form.category,
                            positioning: form.positioning, science_image: form.science_image || "",
                            review_status: form.review_status, reviewer: form.reviewer || "",
                            visible: form.visible !== false, display: form.display || {},
                            seo: form.seo || {},
                            ...(form.use_product_formulation ? {} : { ingredients: form.ingredients || [] }),
                        })}>
                        {save.isPending ? "SAVING…" : "SAVE"}
                    </Btn>
                </div>} />

            {!!(form.flags || []).length && (
                <Card>
                    <div className="flex items-start gap-3" data-testid="science-flags">
                        <AlertTriangle className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                        <div>
                            <div className="font-semibold">Needs your review</div>
                            <ul className="text-sm text-zinc-400 mt-2 space-y-1">
                                {form.flags.map((f, i) => <li key={i}>• {f}</li>)}
                            </ul>
                            <Btn variant="ghost" className="mt-3" disabled={!editable} data-testid="science-clear-flags"
                                onClick={() => save.mutate({ flags: [] })}>MARK REVIEWED</Btn>
                        </div>
                    </div>
                </Card>
            )}

            <Card title="General">
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Traditional system">
                        <Select value={form.traditional_system || "combination"} disabled={!editable}
                            data-testid="science-system" options={meta?.systems || []}
                            onChange={(e) => set({ traditional_system: e.target.value })} />
                    </Field>
                    <Field label="Category">
                        <Input value={form.category || ""} disabled={!editable} data-testid="science-category"
                            onChange={(e) => set({ category: e.target.value })} />
                    </Field>
                    <Field label="Review status">
                        <Select value={form.review_status || "draft"} disabled={!editable}
                            data-testid="science-review" options={meta?.review_statuses || []}
                            onChange={(e) => set({ review_status: e.target.value })} />
                    </Field>
                    <Field label="Positioning / short purpose" className="md:col-span-2">
                        <Input value={form.positioning || ""} disabled={!editable} data-testid="science-positioning"
                            onChange={(e) => set({ positioning: e.target.value })} />
                    </Field>
                    <Field label="Science image URL" hint="Falls back to the product image">
                        <Input value={form.science_image || ""} disabled={!editable} data-testid="science-image"
                            onChange={(e) => set({ science_image: e.target.value })} />
                    </Field>
                    <Field label="Show on the Science page">
                        <Select value={form.visible === false ? "no" : "yes"} disabled={!editable}
                            data-testid="science-visible"
                            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                            onChange={(e) => set({ visible: e.target.value === "yes" })} />
                    </Field>
                    <Field label="Reviewed by">
                        <Input value={form.reviewer || ""} disabled={!editable} data-testid="science-reviewer"
                            onChange={(e) => set({ reviewer: e.target.value })} />
                    </Field>
                </div>
            </Card>

            <Card title="Science content" >
                <p className="text-sm text-zinc-500 mb-4">
                    Leave a field blank and it simply won't appear on the website. Never write a claim that a
                    product treats, prevents or cures anything.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    {TEXT_FIELDS.map(([key, label]) => (
                        <Field key={key} label={label} className={key === "overview" ? "md:col-span-2" : ""}>
                            <Textarea rows={key === "overview" ? 4 : 3} value={form[key] || ""} disabled={!editable}
                                data-testid={`science-${key}`} onChange={(e) => set({ [key]: e.target.value })} />
                        </Field>
                    ))}
                </div>
            </Card>

            <Card title={`Formulation (${(form.ingredients || []).length})`}>
                {form.use_product_formulation && (
                    <p className="text-sm text-gold mb-4">
                        ReDrive Max reads its formulation from Product CRM → ReDrive Max → Formulation, so the
                        two can never drift apart. Combos and doses are edited there.
                    </p>
                )}
                <div className="grid md:grid-cols-3 gap-4 mb-5">
                    <Field label="Public display">
                        <Select value={(form.display || {}).featured_mode || "max"} disabled={!editable}
                            data-testid="science-featured-mode"
                            options={[{ value: "all", label: "Show all ingredients" },
                                { value: "max", label: "Show first N" },
                                { value: "selected", label: "Show only the ones I ticked" }]}
                            onChange={(e) => set({ display: { ...form.display, featured_mode: e.target.value } })} />
                    </Field>
                    <Field label="How many (when 'first N')">
                        <Input type="number" min="1" value={(form.display || {}).featured_max ?? 6} disabled={!editable}
                            data-testid="science-featured-max"
                            onChange={(e) => set({ display: { ...form.display, featured_max: Number(e.target.value) } })} />
                    </Field>
                    <Field label="Full formulation link">
                        <Select value={(form.display || {}).show_full_formulation === false ? "no" : "yes"}
                            disabled={!editable} data-testid="science-show-full"
                            options={[{ value: "yes", label: "Show" }, { value: "no", label: "Hide" }]}
                            onChange={(e) => set({ display: { ...form.display, show_full_formulation: e.target.value === "yes" } })} />
                    </Field>
                </div>
                <IngredientRows rows={form.ingredients || []} combos={combos} editable={!locked}
                    onChange={(rows) => set({ ingredients: rows })} />
            </Card>

            <Card title="SEO">
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="SEO title">
                        <Input value={(form.seo || {}).seo_title || ""} disabled={!editable} data-testid="science-seo-title"
                            onChange={(e) => set({ seo: { ...form.seo, seo_title: e.target.value } })} />
                    </Field>
                    <Field label="Focus keyword">
                        <Input value={(form.seo || {}).focus_keyword || ""} disabled={!editable}
                            onChange={(e) => set({ seo: { ...form.seo, focus_keyword: e.target.value } })} />
                    </Field>
                    <Field label="Meta description">
                        <Input value={(form.seo || {}).meta_description || ""} disabled={!editable}
                            onChange={(e) => set({ seo: { ...form.seo, meta_description: e.target.value } })} />
                    </Field>
                </div>
            </Card>
        </div>
    );
};

const IngredientLibrary = ({ meta, editable }) => {
    const qc = useQueryClient();
    const [q, setQ] = useState("");
    const [openId, setOpenId] = useState(null);
    const { data: rows = [] } = useQuery({
        queryKey: ["science-ingredients"],
        queryFn: async () => (await rhm.get("/science/ingredients")).data,
    });
    const patch = useMutation({
        mutationFn: async ({ id, body }) => (await rhm.patch(`/science/ingredients/${id}`, body)).data,
        onSuccess: () => { toast.success("Ingredient saved"); qc.invalidateQueries({ queryKey: ["science-ingredients"] }); },
        onError: (e) => toast.error(apiError(e)),
    });
    const [draft, setDraft] = useState({});
    const shown = rows.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));

    return (
        <Card title={`Ingredient library (${rows.length})`} action={
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ingredients"
                data-testid="ingredient-search" />
        }>
            <p className="text-sm text-zinc-500 mb-4">
                One record per ingredient, shared by every product that uses it. Vitamins, minerals and amino
                acids come pre-filled with factual nutritional roles; herbs are left blank for your reviewer.
            </p>
            <div className="space-y-2">
                {shown.map((r) => {
                    const open = openId === r.id;
                    const d = { ...r, ...(draft[r.id] || {}) };
                    return (
                        <div key={r.id} className="border border-white/10" data-testid={`ingredient-${r.slug}`}>
                            <button onClick={() => setOpenId(open ? null : r.id)}
                                className="w-full text-left p-3 flex items-center justify-between gap-3">
                                <span className="font-semibold">{r.name}</span>
                                <span className="flex items-center gap-2">
                                    <Badge tone={r.review_status === "needs_review" ? "warn" : "good"}>
                                        {(r.review_status || "").replace("_", " ")}
                                    </Badge>
                                    <span className="mono text-[9px] tracking-[0.14em] text-zinc-500">
                                        {(r.evidence_level || "").replace(/_/g, " ").toUpperCase()}
                                    </span>
                                </span>
                            </button>
                            {open && (
                                <div className="p-4 border-t border-white/10 grid md:grid-cols-2 gap-4">
                                    <Field label="Unani perspective">
                                        <Textarea rows={3} value={d.unani || ""} disabled={!editable}
                                            data-testid={`ingredient-unani-${r.slug}`}
                                            onChange={(e) => setDraft({ ...draft, [r.id]: { ...d, unani: e.target.value } })} />
                                    </Field>
                                    <Field label="Ayurvedic perspective">
                                        <Textarea rows={3} value={d.ayurveda || ""} disabled={!editable}
                                            onChange={(e) => setDraft({ ...draft, [r.id]: { ...d, ayurveda: e.target.value } })} />
                                    </Field>
                                    <Field label="Nutrition / scientific information">
                                        <Textarea rows={3} value={d.nutrition || ""} disabled={!editable}
                                            data-testid={`ingredient-nutrition-${r.slug}`}
                                            onChange={(e) => setDraft({ ...draft, [r.id]: { ...d, nutrition: e.target.value } })} />
                                    </Field>
                                    <Field label="Typical role in formulation">
                                        <Textarea rows={3} value={d.role || ""} disabled={!editable}
                                            onChange={(e) => setDraft({ ...draft, [r.id]: { ...d, role: e.target.value } })} />
                                    </Field>
                                    <Field label="Evidence level">
                                        <Select value={d.evidence_level || "traditional_use"} disabled={!editable}
                                            data-testid={`ingredient-evidence-${r.slug}`} options={meta?.evidence_levels || []}
                                            onChange={(e) => setDraft({ ...draft, [r.id]: { ...d, evidence_level: e.target.value } })} />
                                    </Field>
                                    <Field label="Review status">
                                        <Select value={d.review_status || "needs_review"} disabled={!editable}
                                            options={meta?.review_statuses || []}
                                            onChange={(e) => setDraft({ ...draft, [r.id]: { ...d, review_status: e.target.value } })} />
                                    </Field>
                                    <div className="md:col-span-2">
                                        <Btn disabled={!editable || patch.isPending} data-testid={`ingredient-save-${r.slug}`}
                                            onClick={() => patch.mutate({
                                                id: r.id,
                                                body: {
                                                    unani: d.unani || "", ayurveda: d.ayurveda || "",
                                                    nutrition: d.nutrition || "", role: d.role || "",
                                                    evidence_level: d.evidence_level, review_status: d.review_status,
                                                },
                                            })}>SAVE INGREDIENT</Btn>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

const SettingsTab = ({ editable }) => {
    const qc = useQueryClient();
    const { data } = useQuery({
        queryKey: ["science-settings-crm"],
        queryFn: async () => (await rhm.get("/science/settings")).data,
    });
    const [form, setForm] = useState(null);
    useEffect(() => { if (data) setForm(data); }, [data]);
    const save = useMutation({
        mutationFn: async (body) => (await rhm.put("/science/settings", body)).data,
        onSuccess: () => { toast.success("Science page settings saved"); qc.invalidateQueries({ queryKey: ["science-settings-crm"] }); },
        onError: (e) => toast.error(apiError(e)),
    });
    if (!form) return <Card>Loading…</Card>;
    const set = (patch) => setForm({ ...form, ...patch });

    return (
        <Card title="Science page & SEO" action={
            <Btn disabled={!editable || save.isPending} data-testid="science-settings-save"
                onClick={() => save.mutate({
                    disclaimer: form.disclaimer, hub_eyebrow: form.hub_eyebrow,
                    hub_heading: form.hub_heading, hub_sub: form.hub_sub, seo: form.seo,
                })}>SAVE</Btn>
        }>
            <div className="grid md:grid-cols-3 gap-4">
                <Field label="Hub eyebrow">
                    <Input value={form.hub_eyebrow || ""} disabled={!editable} data-testid="science-hub-eyebrow"
                        onChange={(e) => set({ hub_eyebrow: e.target.value })} />
                </Field>
                <Field label="Hub heading" className="md:col-span-2">
                    <Input value={form.hub_heading || ""} disabled={!editable} data-testid="science-hub-heading"
                        onChange={(e) => set({ hub_heading: e.target.value })} />
                </Field>
                <Field label="Hub sub-line" className="md:col-span-3">
                    <Textarea rows={2} value={form.hub_sub || ""} disabled={!editable}
                        onChange={(e) => set({ hub_sub: e.target.value })} />
                </Field>
                <Field label="Science page disclaimer" className="md:col-span-3">
                    <Textarea rows={4} value={form.disclaimer || ""} disabled={!editable}
                        data-testid="science-disclaimer-text" onChange={(e) => set({ disclaimer: e.target.value })} />
                </Field>
                <Field label="SEO title">
                    <Input value={(form.seo || {}).seo_title || ""} disabled={!editable} data-testid="science-page-seo-title"
                        onChange={(e) => set({ seo: { ...form.seo, seo_title: e.target.value } })} />
                </Field>
                <Field label="Focus keyword">
                    <Input value={(form.seo || {}).focus_keyword || ""} disabled={!editable}
                        onChange={(e) => set({ seo: { ...form.seo, focus_keyword: e.target.value } })} />
                </Field>
                <Field label="Meta description">
                    <Input value={(form.seo || {}).meta_description || ""} disabled={!editable}
                        onChange={(e) => set({ seo: { ...form.seo, meta_description: e.target.value } })} />
                </Field>
            </div>
        </Card>
    );
};

export default function RhmScience() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [tab, setTab] = useState("products");
    const [openSlug, setOpenSlug] = useState(null);
    const editable = can("website.edit");

    const { data: meta } = useQuery({
        queryKey: ["science-meta"], queryFn: async () => (await rhm.get("/science/meta")).data,
    });
    const { data: records = [], isLoading } = useQuery({
        queryKey: ["science-records"], queryFn: async () => (await rhm.get("/science/products")).data,
    });
    const reorder = useMutation({
        mutationFn: async ({ slug, direction }) =>
            (await rhm.post(`/science/products/${slug}/reorder?direction=${direction}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ["science-records"] }),
        onError: (e) => toast.error(apiError(e)),
    });

    if (!can("website.view")) {
        return <Card><div className="text-zinc-400">You do not have access to the Science CMS.</div></Card>;
    }
    if (openSlug) {
        return <ProductEditor slug={openSlug} meta={meta} editable={editable} onClose={() => setOpenSlug(null)} />;
    }

    return (
        <div data-testid="rhm-science">
            <PageHead title="The Science" subtitle="Product science records, the ingredient library and page settings." />
            <div className="flex flex-wrap gap-1 border-b border-white/10 mb-5">
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`science-tab-${t.key}`}
                        className={`mono text-[10px] tracking-[0.16em] px-4 py-3 border-b-2 -mb-px ${
                            tab === t.key ? "border-gold text-gold" : "border-transparent text-zinc-500 hover:text-white"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "products" && (
                <Card title={`Product science records (${records.length})`}>
                    <p className="text-sm text-zinc-500 mb-4">
                        Order here is the order on the public Science page. Product name, image and status always
                        come from Product CRM.
                    </p>
                    {isLoading ? "Loading…" : (
                        <div className="space-y-2">
                            {records.map((r, i) => (
                                <div key={r.id} className="border border-white/10 p-3 flex items-center gap-3"
                                    data-testid={`science-row-${r.product_slug}`}>
                                    <span className="font-display text-xl text-gold/40 w-8">{String(i + 1).padStart(2, "0")}</span>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold flex items-center gap-2">
                                            {r.product?.name}
                                            {!!(r.flags || []).length && (
                                                <span data-testid={`science-flagged-${r.product_slug}`}>
                                                    <Badge tone="warn">needs review</Badge>
                                                </span>
                                            )}
                                            {r.visible === false && <Badge tone="info">hidden</Badge>}
                                        </div>
                                        <div className="mono text-[9px] tracking-[0.14em] text-zinc-500 mt-1">
                                            {(r.product?.status || "").toUpperCase()} · {(r.traditional_system || "").toUpperCase()} ·{" "}
                                            {(r.ingredients || []).length} INGREDIENTS · {(r.review_status || "").replace("_", " ").toUpperCase()}
                                        </div>
                                    </div>
                                    <button disabled={!editable || i === 0} data-testid={`science-up-${r.product_slug}`}
                                        onClick={() => reorder.mutate({ slug: r.product_slug, direction: -1 })}
                                        className="text-zinc-500 hover:text-white disabled:opacity-30">
                                        <ArrowUp className="w-4 h-4" />
                                    </button>
                                    <button disabled={!editable || i === records.length - 1} data-testid={`science-down-${r.product_slug}`}
                                        onClick={() => reorder.mutate({ slug: r.product_slug, direction: 1 })}
                                        className="text-zinc-500 hover:text-white disabled:opacity-30">
                                        <ArrowDown className="w-4 h-4" />
                                    </button>
                                    <Btn variant="ghost" data-testid={`science-open-${r.product_slug}`}
                                        onClick={() => setOpenSlug(r.product_slug)}>EDIT SCIENCE</Btn>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            )}
            {tab === "ingredients" && <IngredientLibrary meta={meta} editable={editable} />}
            {tab === "settings" && <SettingsTab editable={editable} />}
        </div>
    );
}
